import { children, slugs } from '../../contact.meta';

/**
 * Who is writing. The four the hand-off draws, and deliberately the same four values the shared form
 * takes as its `variant` — so the picker's answer IS the form to render, with no mapping in between.
 *
 * They are also the CHILD ROUTE each one is answered on (`/new/contact/$audience`), so the audience
 * survives a link, a bookmark and the back button. It decides the `category` the backend files the
 * lead under, which is why it cannot stay component state: an NGO reaching the company form arrives in
 * Slack as a company lead (TEC-3821 B13).
 *
 * The `$audience` param carries this INTERNAL name, not the localized word — the router's rewrite
 * translates the segment in both directions (`contact.meta.ts`), exactly as it does for the page slug
 * above it. So a `<Link>` names `company` and never has to know which locale the visitor is on.
 */
export const AUDIENCES = ['company', 'ngo', 'donor', 'press'] as const;

export type Audience = (typeof AUDIENCES)[number];

const isAudience = (value: string): value is Audience => (AUDIENCES as readonly string[]).includes(value);

/** The audience a `$audience` param names, or null when it names none — which is the 404. */
export function parseAudience(param: string | undefined): Audience | null {
	return param !== undefined && isAudience(param) ? param : null;
}

/**
 * The NGO panel's two tabs. Only `enquire` has a URL of its own: it is a form, so "send me this page"
 * has to be able to mean it, while `collaborate` is the default and owns the bare `/contacto/ong`.
 *
 * `collaborate` therefore appears here but NOT in `contact.meta.ts` — the meta declares URL segments,
 * and giving the default one would put the same page at two addresses.
 */
export const NGO_TABS = ['collaborate', 'enquire'] as const;

export type NgoTab = (typeof NGO_TABS)[number];

/** The tab a bare audience URL opens on. */
export const DEFAULT_NGO_TAB: NgoTab = 'collaborate';

/**
 * The tab a `$tab` param names under this audience, or null when it names none — which is the 404.
 * Read off the meta's own tree, so a tab with a route but no declared segment cannot exist.
 */
export function parseTab(audience: Audience | null, param: string | undefined): NgoTab | null {
	if (!audience || param === undefined) return null;
	const declared = children[audience]?.children;
	if (!declared || !Object.hasOwn(declared, param)) return null;
	return NGO_TABS.find((tab) => tab === param) ?? null;
}

/**
 * The page's slug per locale, every segment joined: `{ es: 'contacto/ong/consulta' }`.
 *
 * `PageSeo` builds the canonical and every `hreflang` as `${baseUrl}/${locale}/${slug}`, so a slug
 * carrying the separators is all a nested page needs for both to come out right.
 */
export function audiencePageSlugs(audience: Audience, tab?: NgoTab): Record<string, string> {
	const audienceNode = children[audience];
	const tabNode = tab ? audienceNode?.children?.[tab] : undefined;
	return Object.fromEntries(
		Object.entries(slugs).map(([locale, slug]) => {
			const below = [audienceNode?.slugs[locale] ?? audience, ...(tab ? [tabNode?.slugs[locale] ?? tab] : [])];
			return [locale, [slug, ...below].join('/')];
		}),
	);
}
